import { Inject, Injectable, inject } from '@angular/core';
import {
  CARDS_DATA_SERVICE,
  CardSectionUpdateRequest,
  CardsDataService,
  CreatedCardInfo,
} from '@app/app/page-modules/programs/services/create/core/cards-data.service';
import { FILE_UPLOAD_SUPPORTED_TYPES, TEXT_FORM_MIN_WORDS_LIMIT } from '@app/app/shared/constants/constants';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import {
  BookingFormContent,
  CheckboxFormContent,
  FileUploadBoxFormContent,
  Form,
  FormContent,
  QuizFormContent,
  TextBoxFormContent,
} from '@app/app/shared/models/editor/form-content.model';
import { ErrorResult, Result, SuccessResult } from '@app/app/shared/store';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { CardPrefillType } from './add-content-catalog.model';

export interface CreatedCardWithPrefill {
  info: CreatedCardInfo;
  prefillApplied: boolean;
}

@Injectable()
export class AddContentCardFactoryService {
  private transloco = inject(TranslocoService);

  constructor(@Inject(CARDS_DATA_SERVICE) private cardsDataService: CardsDataService) {}

  createCard(
    playlistUid: string,
    sectionUid: string,
    options?: { prefill?: CardPrefillType; headline?: string },
  ): Observable<Result<CreatedCardWithPrefill>> {
    return this.cardsDataService.createEmptyCard(playlistUid, sectionUid).pipe(
      switchMap((result) => {
        if (!result.isSuccess || !result.value) {
          return of(new ErrorResult(result.error) as Result<CreatedCardWithPrefill>);
        }

        const info = result.value;
        const prefill = options?.prefill;
        if (!prefill) {
          return of(new SuccessResult<CreatedCardWithPrefill>({ info, prefillApplied: true }));
        }
        if (!info.sectionUid) {
          return of(new SuccessResult<CreatedCardWithPrefill>({ info, prefillApplied: false }));
        }

        return this.prefillCard(info.cardUid, info.sectionUid, prefill, options?.headline).pipe(
          map((prefillApplied) => new SuccessResult<CreatedCardWithPrefill>({ info, prefillApplied })),
        );
      }),
    );
  }

  prefillCard(cardUid: string, sectionUid: string, prefill: CardPrefillType, headline?: string): Observable<boolean> {
    return this.cardsDataService
      .updateCardSection(cardUid, sectionUid, this.buildPrefillRequest(sectionUid, prefill, headline))
      .pipe(map((update) => update.isSuccess));
  }

  private buildPrefillRequest(sectionUid: string, prefill: CardPrefillType, headline?: string): CardSectionUpdateRequest {
    return {
      uid: sectionUid,
      dynamicContent: [this.buildPrefillForm(prefill)],
      languageCode: LanguageCodeHelper.getContentLanguageCode(),
      cardHeadline: headline,
    };
  }

  private buildPrefillForm(prefill: CardPrefillType): Form {
    return {
      uid: uuidv4(),
      type: 'FORM',
      newAddedForm: false,
      content: this.buildPrefillFormContent(prefill),
    };
  }

  private buildPrefillFormContent(prefill: CardPrefillType): FormContent {
    const base = { submitted: false, formIsActive: false, forceExpand: true };

    switch (prefill) {
      case 'QUIZ':
        return {
          ...base,
          type: 'QUIZ',
          title: this.transloco.translate('translations.tiptap.advanced.quiz'),
          quizId: '',
        } as QuizFormContent;
      case 'TEXTBOX':
        return {
          ...base,
          type: 'TEXTBOX',
          title: this.transloco.translate('translations.tiptap.forms.textbox'),
          wordLimit: 500,
          minWordsLimit: TEXT_FORM_MIN_WORDS_LIMIT,
          answerType: 'NORMAL',
          aiAssistantEnabled: false,
          aiAssistantSettingsUid: '',
          isTemplate: false,
        } as TextBoxFormContent;
      case 'USER_FILE_UPLOAD':
        return {
          ...base,
          type: 'USER_FILE_UPLOAD',
          title: this.transloco.translate('translations.tiptap.forms.fileUpload'),
          maxFileSizeMb: 25,
          supportedFileTypes: FILE_UPLOAD_SUPPORTED_TYPES,
          required: false,
        } as FileUploadBoxFormContent;
      case 'CHECKBOX':
        return {
          ...base,
          type: 'CHECKBOX',
          title: this.transloco.translate('translations.tiptap.forms.checkbox'),
          required: false,
        } as CheckboxFormContent;
      case 'BOOKING':
        return {
          ...base,
          type: 'BOOKING',
          title: this.transloco.translate('translations.tiptap.forms.booking'),
          bookableAppointmentId: null,
          hostName: '',
          hostRole: '',
          meetingType: 'in_person',
          location: '',
          sessionDuration: 30,
          availabilityType: 'weekly_recurring',
          weeklySlots: '[]',
          blockDates: '[]',
          instructionsForLearner: '',
          notifyStudent: true,
          notifyAdmin: false,
          addToHostCalendar: false,
          maxParticipants: 1,
        } as BookingFormContent;
      default:
        throw new Error(`Unsupported card prefill type: ${prefill}`);
    }
  }
}
